<template>
  <div class="ds-strip" @wheel="onWheel">
    <button
      v-for="(photo, index) in photos"
      :key="photo.key ?? index"
      type="button"
      class="ds-strip__item"
      :class="{ 'ds-strip__item--wide': photos.length === 1 }"
      :aria-label="$t('utils.detail_sheet_photo_strip.open_photo_number_of_total', { number: index + 1, total: photos.length })"
      @click="$emit('open', index)"
    >
      <img :src="photo.thumbUrl" :alt="$t('utils.detail_sheet_photo_strip.photo_number', { number: index + 1 })" loading="lazy" />
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import type { HighlightPhoto } from '../../helpers/highlightDetails';

export default defineComponent({
  name: 'DetailSheetPhotoStrip',
  props: {
    photos: { type: Array as PropType<HighlightPhoto[]>, default: () => [] },
  },
  emits: ['open'],
  methods: {
    // A trackpad's horizontal gesture should scroll the strip, but a vertical
    // one should keep scrolling the dialog behind it. The previous carousel
    // swallowed every wheel event (and only after DOMContentLoaded, so in
    // practice never at all).
    onWheel(event: WheelEvent) {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        event.stopPropagation();
      }
    },
  },
});
</script>

<style scoped>
.ds-strip {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /* Bleed to the dialog edges so the strip reads as scrollable content. */
  padding: 0 1rem;
  margin: 0 -1rem;
}

.ds-strip::-webkit-scrollbar {
  display: none;
}

.ds-strip__item {
  flex: 0 0 auto;
  width: 45%;
  max-width: 220px;
  aspect-ratio: 1 / 1;
  padding: 0;
  border: 0;
  border-radius: 0.625rem;
  overflow: hidden;
  background: var(--gray);
  scroll-snap-align: start;
}

/* A lone photo gets the full width instead of sitting in a lopsided half. */
.ds-strip__item--wide {
  width: 100%;
  max-width: none;
  aspect-ratio: 16 / 10;
}

.ds-strip__item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s ease;
}

.ds-strip__item:hover img {
  transform: scale(1.04);
}
</style>
